import { Close, DragIndicator } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import { useAiAgent } from 'contexts/AiAgentContext';
import { Row } from 'further-ui/components/Layout';
import React, { useEffect, useRef, useState } from 'react';
import AgentToast from './components/AgentToast';
import ChatInput from './components/ChatInput';
import MessageList from './components/MessageList';
import ResizeHandles from './components/ResizeHandles';
import { useChatMessages } from './hooks/useChatMessages';
import { useWindowBehavior } from './hooks/useWindowBehavior';

export type { PendingInvoice } from './types';

const Shell = styled('div')(({ theme }) => ({
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  borderRadius: 16,
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 0 50px 0 rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.divider}`,
  userSelect: 'none',
}));

const ContentCard = styled('div')({
  flex: 1,
  overflow: 'clip',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

const CardHeader = styled(Row)(({ theme }) => ({
  margin: theme.spacing(6),
  marginTop: theme.spacing(5),
  marginBottom: 0,
  paddingBottom: theme.spacing(5),
  cursor: 'grab',
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:active': { cursor: 'grabbing' },
}));

const AiAgent: React.FC = () => {
  const { isOpen, close } = useAiAgent();
  const hasOpened = useRef(false);
  if (isOpen) {
    hasOpened.current = true;
  }
  const [isDragging, setIsDragging] = useState(false);
  const [toastMessage] = useState<string | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    messages,
    inputText,
    setInputText,
    isLoading,
    stagedFile,
    setStagedFile,
    sendMessage,
    stageFile,
    handleFileInputChange,
    messagesEndRef,
    inputRef,
    fileInputRef,
    snapToBottom,
  } = useChatMessages();

  const autoSendFileInputRef = useRef<HTMLInputElement>(null);

  const { pos, size, handleDragBarMouseDown, makeResizeHandler } =
    useWindowBehavior({
      isOpen,
      snapToBottom,
      inputRef,
    });

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (
      !(
        e.relatedTarget instanceof Node &&
        e.currentTarget.contains(e.relatedTarget)
      )
    )
      setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) stageFile(file);
  };

  if (!hasOpened.current && !toastMessage) {
    return null;
  }

  return (
    <>
      {toastMessage && <AgentToast message={toastMessage} />}
      <Shell
        style={{
          display: isOpen ? 'flex' : 'none',
          left: pos.x,
          top: pos.y,
          width: size.width,
          height: size.height,
        }}
      >
        <ContentCard
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CardHeader
            justify="space-between"
            centered
            onMouseDown={handleDragBarMouseDown}
          >
            <Row centered spacing="xs">
              <GradientAiIcon size={20} />
              <Typography variant="h6">AI Agent</Typography>
            </Row>
            <Row centered spacing="xs">
              <DragIndicator fontSize="small" />
              <StyledIconButton
                size="small"
                onClick={close}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Close sx={{ fontSize: 16 }} />
              </StyledIconButton>
            </Row>
          </CardHeader>

          <input
            type="file"
            ref={autoSendFileInputRef}
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) sendMessage('', file);
              e.target.value = '';
            }}
          />
          <MessageList
            messages={messages}
            isLoading={isLoading}
            messagesEndRef={messagesEndRef}
            onSend={sendMessage}
            onAttachClick={() => autoSendFileInputRef.current?.click()}
            hasInput={!!inputText.trim() || !!stagedFile}
          />

          <ChatInput
            inputText={inputText}
            onInputChange={setInputText}
            onSend={sendMessage}
            isLoading={isLoading}
            stagedFile={stagedFile}
            onClearFile={() => setStagedFile(null)}
            fileInputRef={fileInputRef}
            inputRef={inputRef}
            isDragging={isDragging}
            onFileChange={handleFileInputChange}
            onAttachClick={() => fileInputRef.current?.click()}
          />
        </ContentCard>

        <ResizeHandles makeResizeHandler={makeResizeHandler} />
      </Shell>
    </>
  );
};

export default AiAgent;
